import { ArrowRight, Calendar, HelpCircle, ShieldCheck, Sliders, Users } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/Layout";
import { useLandscapes, cutoverStore } from "../../store/cutoverPlan";
import { InfoTip } from "../components/Erklaerung";
import { Ampel, Badge, Card } from "../components/ui";
import { useApp } from "../../store/AppStore";
import { assignedLandscapes, landscapes } from "../../data/landscapes";
import { landscapeRisk, sumLandscapePersons } from "../../domain/aggregate";
import { gateChain, landscapeDemand, multiClientStats } from "../../domain/analysis";
import { LizenzBlock, OffeneEngineZuordnung } from "../components/Lizenz";
import { fueLandscapes, isFueRelevantLandscape, nonFueLandscapes } from "../../data/licensing";
import { Fragment, useMemo, type ReactNode } from "react";
import { formatDateDe, formatFue, formatNum, formatPct } from "../../domain/rules";
import type { Ampel as AmpelType } from "../../domain/types";

const KEY_LABEL: Record<string, string> = { username: "Benutzername", personalnummer: "Personalnummer", email: "E-Mail", custom: "freies Feld" };
const AMPEL_LABEL: Record<AmpelType, string> = { gruen: "unkritisch", gelb: "beobachten", rot: "kritisch", grau: "offen" };
const AMPEL_BADGE: Record<AmpelType, "green" | "amber" | "red" | "gray"> = { gruen: "green", gelb: "amber", rot: "red", grau: "gray" };

/**
 * Ampel je Landschaft aus landscapeRisk (Anteil über-berechtigter Personen).
 * Fehlerbehebung (a): Wenn alle zugeordneten Landschaften auf dieselbe Ampel
 * fallen (früher wirkte alles gelb), werden die Schwellen sichtbar gestaffelt —
 * die Landschaften werden nach ihrem Anteil in Terzile gruen/gelb/rot verteilt.
 */
function ampelByLandscape(persons: import("../../domain/types").Person[]): {
  ampel: Record<string, AmpelType>;
  risk: ReturnType<typeof landscapeRisk>;
  staggered: boolean;
} {
  const risk = landscapeRisk(persons);
  // AP1: Landschaften ohne FUE-Metrik werden nicht bewertet und nicht mitgestaffelt.
  const ids = assignedLandscapes.filter((l) => isFueRelevantLandscape(l.id)).map((l) => l.id);
  const distinct = new Set(ids.map((id) => risk[id]?.ampel ?? "grau"));
  const ampel: Record<string, AmpelType> = {};

  for (const l of assignedLandscapes) if (!isFueRelevantLandscape(l.id)) ampel[l.id] = "grau";

  if (distinct.size > 1) {
    for (const id of ids) ampel[id] = risk[id]?.ampel ?? "grau";
    return { ampel, risk, staggered: false };
  }

  // Sichtbare Staffelung: nach Anteil aufsteigend sortieren, in Terzile teilen.
  const sorted = [...ids].sort((a, b) => (risk[a]?.share ?? 0) - (risk[b]?.share ?? 0));
  const n = sorted.length;
  sorted.forEach((id, i) => {
    const frac = n > 1 ? i / (n - 1) : 0;
    ampel[id] = frac < 1 / 3 ? "gruen" : frac < 2 / 3 ? "gelb" : "rot";
  });
  return { ampel, risk, staggered: true };
}

/** Eine Kennzahl-Kachel innerhalb des Landschafts-Wasserfalls. */
function Stat({ label, value, tone, info, sub }: { label: string; value: string; tone?: "current" | "clean"; info?: ReactNode; sub?: ReactNode }) {
  const color = tone === "current" ? "var(--color-amp-red)" : tone === "clean" ? "var(--color-amp-green)" : "var(--text-strong)";
  return (
    <div className="min-w-0 flex-1">
      <div className="text-faint inline-flex items-center gap-1 text-[11px] font-medium tracking-wide uppercase">{label}{info && <InfoTip size={11}>{info}</InfoTip>}</div>
      <div className="tnum mt-0.5 text-xl font-semibold" style={{ color }}>{value}</div>
      {sub && <div className="text-faint tnum mt-0.5 text-xs">{sub}</div>}
    </div>
  );
}

export function Landschaften() {
  const effektiv = useLandscapes();
  const cutoverOf = (id: string) => effektiv.find((x) => x.id === id)?.cutover;
  const { ds, identity, identityKey, kpis, ratio } = useApp();
  const [params] = useSearchParams();
  const sel = params.get("ls");

  const demand = landscapeDemand(ds);
  // Gate-Kette mit den wirksamen Cutover-Daten: was von der Landschaft am Gate erstmals zählt (R1).
  const gateOf = useMemo(() => new Map(gateChain(ds, ratio, effektiv.filter((l) => !l.open)).map((g) => [g.landscapeId, g])), [ds, ratio, effektiv]);
  // Personen mit Konten in mehreren Mandanten je Landschaft (führendes System).
  const mc = multiClientStats(ds);
  const mcByLid: Record<string, { persons: number; maxClients: number }> = {};
  for (const x of Object.values(mc.bySid)) { const lid = assignedLandscapes.find((l) => l.systems[0] === x.sid)?.id; if (lid) mcByLid[lid] = { persons: x.persons, maxClients: x.maxClients }; }
  const { ampel, risk, staggered } = ampelByLandscape(ds.persons);

  const sumLand = sumLandscapePersons(ds.persons);
  const consolidated = identity.recognizedPersons;
  const sumLandFue = Object.values(demand).reduce((s, d) => s + d.fueCurrent, 0);

  return (
    <div>
      <PageHeader route="/landschaften" />

      {/* R1: warum Σ je Landschaft ≠ konsolidiert */}
      <div className="mb-4 rounded-lg border-soft p-4" style={{ borderWidth: 1, backgroundColor: "var(--surface-sunken)" }}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="text-normal inline-flex items-center gap-1 font-medium">
            Konsolidierung (R1)
            <InfoTip>
              Eine Person in mehreren Landschaften zählt gesamt einmal; wie sauber zusammengeführt wird, hängt am Identitätsschlüssel ({KEY_LABEL[identityKey] ?? identityKey}).
              Der Bedarf einer Person erscheint in jeder ihrer Landschaften, im Cockpit zählt er genau einmal.
              {" "}{nonFueLandscapes.map((l) => l.name).join(" und ")} tragen keinen FUE-Bedarf (eigene Lizenzmetrik); die meisten Personen dort zählen bereits über ein Konto auf einem FUE-System.
            </InfoTip>
          </span>
          <span className="text-dim">Personen: Σ je Landschaft <span className="tnum text-normal">{formatNum(sumLand)}</span> · konsolidiert <span className="tnum text-normal">{formatNum(consolidated)}</span> · Doppelzählung vermieden <span className="tnum text-normal">{formatNum(sumLand - consolidated)}</span></span>
          <span className="text-dim">FUE: Σ je Landschaft <span className="tnum text-normal">{formatNum(sumLandFue)}</span> · konsolidiert <span className="tnum text-normal">{formatNum(kpis.ist)}</span> · Doppelzählung vermieden <span className="tnum text-normal">{formatNum(sumLandFue - kpis.ist)}</span></span>
        </div>
      </div>

      <div className="mb-4">
        <OffeneEngineZuordnung />
      </div>

      {staggered && (
        <div className="mb-4 rounded-lg p-3 text-xs" style={{ borderWidth: 1, borderColor: "var(--color-amp-amber)", backgroundColor: "var(--surface-sunken)" }}>
          <span className="text-normal inline-flex items-center gap-1 font-medium">Ampel-Staffelung aktiv <InfoTip>Die Anteile über-berechtigter Personen liegen dicht beieinander. Die Ampeln sind deshalb nach relativem Anteil (Terzile) gestaffelt statt an festen Schwellen.</InfoTip></span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Paket 6: Engine-/Sondermetrik-Systeme gehören optisch nicht zwischen die FUE-Landschaften —
            sie werden nach anderer Logik abgerechnet. Deshalb zuerst FUE, dann L11, dann ein eigener Block. */}
        {[...fueLandscapes, ...landscapes.filter((l) => l.open), ...nonFueLandscapes].map((l) => {
          const highlighted = sel === l.id;

          // L11 — Zuordnung offen: keine Kennzahlen, kein Absprung.
          if (l.open) {
            return (
              <Card key={l.id} className={highlighted ? "ring-2 ring-[var(--color-brand-500)]" : ""}>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5"><HelpCircle size={18} className="text-faint" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-strong text-[15px] font-semibold">L11</span>
                      <Badge tone="gray">Zuordnung offen</Badge>
                    </div>
                    <div className="text-dim mt-1 text-sm">RFP: 11 Landschaften · Migrationsplan: 10 Gruppen <InfoTip>Welche Landschaft on-prem bleibt, ist im Termin zu klären.</InfoTip></div>
                  </div>
                </div>
              </Card>
            );
          }

          const d = demand[l.id] ?? { persons: 0, fueCurrent: 0, fueClean: 0, optimizable: 0, optimizableSafe: 0, optimizableAlone: 0, fueRelevant: true };
          const a = ampel[l.id] ?? "grau";
          const shareTxt = formatPct((risk[l.id]?.share ?? 0) * 100, 0);
          const fueRelevant = isFueRelevantLandscape(l.id);

          return (
            <Fragment key={l.id}>
            {!fueRelevant && l.id === nonFueLandscapes[0]?.id && (
              <div className="col-span-full mt-4">
                <h2 className="text-strong inline-flex items-center gap-1.5 text-lg font-semibold">Engine- und Sondermetriken (nicht FUE) <InfoTip>Diese Systeme werden nicht über Berechtigungsklassen abgerechnet. Ihr Lizenzbedarf steht in einer eigenen Metrik, nicht in FUE. Sie erscheinen hier, damit die Landschaft vollständig ist.</InfoTip></h2>
              </div>
            )}
            <Card
              className={highlighted ? "ring-2 ring-[var(--color-brand-500)]" : ""}
              title={
                <span className="inline-flex items-center gap-2">
                  <Ampel state={a} size={11} />
                  {l.name}
                </span>
              }
              subtitle={`${l.group} · ${l.systems.join(", ")}`}
              right={
                <div className="text-right">
                  {fueRelevant ? (
                    <>
                      <Badge tone={AMPEL_BADGE[a]}>{AMPEL_LABEL[a]}</Badge>
                      <div className="text-faint mt-1 text-[11px]">{shareTxt} über-berechtigt</div>
                    </>
                  ) : (
                    <>
                      <Badge tone="gray">nicht bewertet</Badge>
                      <div className="text-faint mt-1 text-[11px]">keine FUE-Metrik</div>
                    </>
                  )}
                </div>
              }
            >
              {/* AP1: Ohne FUE-Metrik gibt es hier keinen FUE-Bedarf — und deshalb auch
                  keine Zahlen, die man optimieren könnte. Die Landschaft bleibt sichtbar. */}
              {!fueRelevant && (
                <div
                  className="rounded-xl p-3 text-sm"
                  style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
                >
                  <div className="text-normal inline-flex items-center gap-1 font-medium">kein FUE-Bedarf · eigene Lizenzmetrik <InfoTip>Diese Landschaft wird nicht über Berechtigungsklassen abgerechnet und trägt keinen FUE-Bedarf bei. Personen und Berechtigungen bleiben nachvollziehbar.</InfoTip></div>
                </div>
              )}

              {/* B1: Der Cutover senkt den Bedarf NICHT — nach der Migration sind es fueCurrent;
                  nur vorherige Optimierung führt auf fueClean. Drei Werte, Pfeil zwischen den letzten beiden. */}
              {fueRelevant && (
              <>
              <div className="bg-sunken flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border-soft p-3" style={{ borderWidth: 1 }}>
                <Stat
                  label="Bedarf der Landschaft (alle Personen mit Bezug)"
                  value={formatFue(d.fueCurrent)}
                  tone="current"
                  info="Am Gate zählt davon nur, wer hier erstmals zählt (R1); die Gate-Seite zeigt diesen Anteil."
                  sub={gateOf.get(l.id) ? <>davon neu am Gate: {formatFue(gateOf.get(l.id)!.fueAdded)}</> : undefined}
                />
                <Stat label="vermeidbar · allein" value={`−${formatFue(d.optimizableAlone)}`} />
                <Stat label="vermeidbar · im Verbund" value={`−${formatFue(d.optimizable)}`} />
                <ArrowRight size={20} className="text-faint shrink-0" aria-hidden />
                <Stat label="Bedarf nach Optimierung" value={formatFue(d.fueClean)} tone="clean" />
              </div>
              <div className="text-dim mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                <ShieldCheck size={13} style={{ color: "var(--color-amp-green)" }} />
                <span>davon gefahrlos (nie genutzt): <span className="tnum font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(d.optimizableSafe)}</span></span>
                <InfoTip size={12}>
                  Der Cutover senkt den Bedarf nicht, nur vorherige Optimierung.
                  {d.optimizableAlone < d.optimizable - 0.5 && <> Allein bringt diese Landschaft nur −{formatFue(d.optimizableAlone)}: Konten in anderen Systemen und Mandanten halten die übrigen Personen oben (R2).</>}
                </InfoTip>
              </div>
              </>
              )}

              {/* Meta-Zeile: Personen · Cutover — sauber getrennt (Fehler b: Leerzeichen) */}
              <div className="text-dim mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <Users size={13} className="text-faint" />
                  <span className="tnum">{formatNum(d.persons)}</span>
                  <span>Personen mit Bezug</span>
                  {(mcByLid[l.id]?.persons ?? 0) >= 50 && (
                    <span className="text-faint">· {formatNum(mcByLid[l.id].persons)} in bis zu {mcByLid[l.id].maxClients} Mandanten</span>
                  )}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={13} className="text-faint" />
                  <span>Cutover</span>
                  <span className="tnum text-normal">{formatDateDe(cutoverOf(l.id) ?? l.cutover)}</span>
                  {cutoverStore.isOverridden(l.id) && <span className="text-faint">· geändert, RFP-Plan {formatDateDe(l.cutover)}</span>}
                </span>
                <span className="text-faint">{l.wave}</span>
              </div>

              {/* AP1: Lizenzmodell je System — was zählt hier wie? */}
              <LizenzBlock landscapeId={l.id} compact={!fueRelevant} />

              {/* Absprung in die Optimierung dieser Landschaft */}
              {fueRelevant && (
              <Link
                to={`/optimierung?ls=${l.id}`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                style={{ color: "var(--color-brand-600)" }}
              >
                <Sliders size={15} />
                Optimierung dieser Landschaft
                <ArrowRight size={15} />
              </Link>
              )}
            </Card>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
